/**
 * Platform list - Figma `6377:87324` (Online Tools), rows `6377:87332`.
 *
 * A heading and a lead with a call to action beside it, then a hairline-separated list:
 * a screenshot on the left, title and body in the middle, an arrow on the right that
 * makes the whole row a link.
 *
 * **Measured 2026-09-26**: the heading h1 over at most two thirds of the page, the lead
 * body-medium; a row is a 354x200 picture, 180 of space, then an h4 title over a
 * body-medium paragraph at most 658 wide, and the file's diagonal arrow in its grey disc.
 *
 * **The section gained `cta`** - the design puts a button next to the lead and the model
 * had nowhere for it.
 *
 * Two deviations worth knowing:
 *
 * - **The heading is a plain string, so it cannot be two-tone.** In the design the words
 *   "narzędzia" and "online" are blue and green inside an otherwise black heading. Making
 *   `heading` rich text would buy that at the price of editors putting lists and links in
 *   an `<h2>`. Worth a decision: if inline colour is wanted across the site, it needs a
 *   convention, not a per-section fix.
 * - ~~The row arrow is the exported horizontal arrow in a ring.~~ The diagonal "opens
 *   elsewhere" arrow is exported now (`arrow-circle-up-right`) and drawn as the file has it.
 */
import { CtaLink, Eyebrow, SectionShell, withHighlight } from '../primitives';
import { Icon } from '../../components/ui/Icon';
import type { Card, PlatformListSection } from '../../types/sections';
import { Image, SIZES } from '../../components/ui/Image';
import { AppLink } from '../../components/ui/AppLink';

function Row({ card }: { card: Card }) {
  const inner = (
    <>
      <figure className="m-0 aspect-[354/200] w-full shrink-0 overflow-hidden rounded-l bg-surface-muted sm:w-[354px]">
        {card.image ? (
          <Image
            media={card.image}
            alt={card.image.alternativeText ?? card.title ?? ''}
            sizes={SIZES.half}
            className="h-full w-full object-cover"
          />
        ) : null}
      </figure>

      <div className="flex flex-1 items-start justify-between gap-6">
        <div className="flex max-w-[658px] flex-col gap-8">
          <h3 className="text-h4 text-ink-strong">{card.title}</h3>
          {card.body ? <p className="text-body-md text-ink-strong">{card.body}</p> : null}
        </div>

        {card.url ? (
          <Icon name="arrow-circle-up-right" size={48} className="text-grey-700 transition group-hover:text-ink" />
        ) : null}
      </div>
    </>
  );

  const className = 'group flex flex-col items-start gap-6 border-t border-grey-500 py-8 sm:flex-row sm:items-center lg:gap-[180px]';
  return card.url
    ? <AppLink to={card.url} className={className}>{inner}</AppLink>
    : <article className={className}>{inner}</article>;
}

export default function PlatformList({ data }: { data: PlatformListSection }) {
  const platforms = data.platforms ?? [];

  return (
    <SectionShell settings={data.settings} name="platform-list">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {data.heading ? <h2 className="text-h2 text-ink lg:max-w-[66.666%] lg:text-h1">{withHighlight(data.heading)}</h2> : null}

      {data.body || data.cta ? (
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-16">
          {data.body ? <p className="max-w-2xl whitespace-pre-line text-body-md text-ink-muted">{data.body}</p> : null}
          {data.cta ? <CtaLink value={data.cta} /> : null}
        </div>
      ) : null}

      {platforms.length ? (
        <div className="mt-6 flex flex-col border-b border-grey-500">
          {platforms.map((card, i) => <Row key={i} card={card} />)}
        </div>
      ) : null}
    </SectionShell>
  );
}
